import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Calculator, MapPin, Home, Building2, Hammer, ArrowRight, 
  CheckCircle, AlertCircle, Phone, Info, Plus, Trash2, X,
  Wrench, Package
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_de12da21-0869-4a9b-8efe-c2e2b4d72064/artifacts/km3uhq32_YGK-CONSTRUCTION.png";

// Cost split: 60% Labor, 40% Materials
const LABOR_SPLIT = 0.60;
const MATERIAL_SPLIT = 0.40;

// Trade definitions with base rates per sq ft (total cost before split)
const TRADES = {
  framing: {
    name: "Framing",
    icon: "🏗️",
    services: [
      { id: "wall_framing", name: "Wall Framing", baseRate: 8 },
      { id: "floor_framing", name: "Floor Framing", baseRate: 10 },
      { id: "roof_framing", name: "Roof Framing", baseRate: 12 },
      { id: "deck_framing", name: "Deck Framing", baseRate: 15 },
    ],
    qualityMultipliers: { basic: 0.85, standard: 1.0, premium: 1.35 }
  },
  electrical: {
    name: "Electrical",
    icon: "⚡",
    services: [
      { id: "rough_in", name: "Rough-In Wiring", baseRate: 6 },
      { id: "panel_upgrade", name: "Panel Upgrade", baseRate: 8 },
      { id: "finish_electrical", name: "Finish Electrical", baseRate: 5 },
      { id: "lighting_install", name: "Lighting Installation", baseRate: 7 },
    ],
    qualityMultipliers: { basic: 0.85, standard: 1.0, premium: 1.40 }
  },
  plumbing: {
    name: "Plumbing",
    icon: "🔧",
    services: [
      { id: "rough_plumbing", name: "Rough-In Plumbing", baseRate: 12 },
      { id: "fixture_install", name: "Fixture Installation", baseRate: 10 },
      { id: "water_heater", name: "Water Heater Install", baseRate: 15 },
      { id: "drain_lines", name: "Drain Line Work", baseRate: 14 },
    ],
    qualityMultipliers: { basic: 0.85, standard: 1.0, premium: 1.45 }
  },
  hvac: {
    name: "HVAC",
    icon: "❄️",
    services: [
      { id: "ductwork", name: "Ductwork Installation", baseRate: 8 },
      { id: "ac_install", name: "AC Unit Installation", baseRate: 18 },
      { id: "furnace_install", name: "Furnace Installation", baseRate: 16 },
      { id: "mini_split", name: "Mini-Split System", baseRate: 20 },
    ],
    qualityMultipliers: { basic: 0.85, standard: 1.0, premium: 1.50 }
  },
  drywall: {
    name: "Drywall",
    icon: "🧱",
    services: [
      { id: "hang_drywall", name: "Hang Drywall", baseRate: 2.5 },
      { id: "tape_finish", name: "Tape & Finish", baseRate: 2 },
      { id: "texture", name: "Texture Application", baseRate: 1.5 },
      { id: "repair", name: "Drywall Repair", baseRate: 4 },
    ],
    qualityMultipliers: { basic: 0.90, standard: 1.0, premium: 1.30 }
  },
  flooring: {
    name: "Flooring",
    icon: "🪵",
    services: [
      { id: "hardwood", name: "Hardwood Installation", baseRate: 12 },
      { id: "tile", name: "Tile Installation", baseRate: 15 },
      { id: "lvp", name: "LVP/Laminate Install", baseRate: 6 },
      { id: "carpet", name: "Carpet Installation", baseRate: 4 },
    ],
    qualityMultipliers: { basic: 0.80, standard: 1.0, premium: 1.60 }
  },
  painting: {
    name: "Painting",
    icon: "🎨",
    services: [
      { id: "interior_paint", name: "Interior Painting", baseRate: 3 },
      { id: "exterior_paint", name: "Exterior Painting", baseRate: 4 },
      { id: "cabinet_paint", name: "Cabinet Painting", baseRate: 8 },
      { id: "staining", name: "Wood Staining", baseRate: 5 },
    ],
    qualityMultipliers: { basic: 0.85, standard: 1.0, premium: 1.35 }
  },
  roofing: {
    name: "Roofing",
    icon: "🏠",
    services: [
      { id: "shingle_roof", name: "Shingle Roofing", baseRate: 5 },
      { id: "metal_roof", name: "Metal Roofing", baseRate: 12 },
      { id: "flat_roof", name: "Flat Roof System", baseRate: 8 },
      { id: "roof_repair", name: "Roof Repair", baseRate: 10 },
    ],
    qualityMultipliers: { basic: 0.85, standard: 1.0, premium: 1.50 }
  },
  concrete: {
    name: "Concrete",
    icon: "🧱",
    services: [
      { id: "slab", name: "Concrete Slab", baseRate: 8 },
      { id: "driveway", name: "Driveway Pour", baseRate: 10 },
      { id: "foundation", name: "Foundation Work", baseRate: 15 },
      { id: "sidewalk", name: "Sidewalk/Patio", baseRate: 9 },
    ],
    qualityMultipliers: { basic: 0.90, standard: 1.0, premium: 1.35 }
  },
  insulation: {
    name: "Insulation",
    icon: "🧤",
    services: [
      { id: "batt_insulation", name: "Batt Insulation", baseRate: 1.5 },
      { id: "blown_in", name: "Blown-In Insulation", baseRate: 2 },
      { id: "spray_foam", name: "Spray Foam", baseRate: 4 },
      { id: "rigid_board", name: "Rigid Board", baseRate: 3 },
    ],
    qualityMultipliers: { basic: 0.85, standard: 1.0, premium: 1.45 }
  },
};

const QUALITY_LABELS = {
  basic: { label: "Basic", description: "Economy materials, functional" },
  standard: { label: "Standard", description: "Mid-grade materials, good quality" },
  premium: { label: "Premium", description: "High-end materials, luxury finish" }
};

// Oklahoma City area zip codes
const OKC_PREFIXES = ['730', '731', '732', '733', '734', '735', '736', '737', '738', '739', '740', '741'];

const EstimatorPage = () => {
  const [step, setStep] = useState(1);
  const [projectType, setProjectType] = useState("");
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [zipCode, setZipCode] = useState("");
  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    phone: "",
    details: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const projectTypes = [
    { value: "renovation", label: "Home Renovation", icon: Hammer },
    { value: "new_build", label: "New Construction", icon: Home },
    { value: "commercial", label: "Commercial Project", icon: Building2 },
    { value: "addition", label: "Home Addition", icon: Home },
  ];

  const isOKCArea = useMemo(() => {
    const prefix = zipCode.replace(/\D/g, '').substring(0, 3);
    return OKC_PREFIXES.some(p => prefix.startsWith(p.substring(0, 2)));
  }, [zipCode]);

  const addTrade = (tradeId) => {
    if (selectedTrades.find(t => t.tradeId === tradeId)) {
      toast.error("This trade is already added");
      return;
    }
    setSelectedTrades([...selectedTrades, {
      id: Date.now(),
      tradeId,
      services: [],
      quality: "standard",
      sqft: 500
    }]);
  };

  const removeTrade = (id) => {
    setSelectedTrades(selectedTrades.filter(t => t.id !== id));
  };

  const updateTrade = (id, field, value) => {
    setSelectedTrades(selectedTrades.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const toggleService = (tradeId, serviceId) => {
    setSelectedTrades(selectedTrades.map(t => {
      if (t.id === tradeId) {
        const services = t.services.includes(serviceId)
          ? t.services.filter(s => s !== serviceId)
          : [...t.services, serviceId];
        return { ...t, services };
      }
      return t;
    }));
  };

  const removeService = (tradeId, serviceId) => {
    setSelectedTrades(selectedTrades.map(t => {
      if (t.id === tradeId) {
        return { ...t, services: t.services.filter(s => s !== serviceId) };
      }
      return t;
    }));
  };

  // Calculate trade estimate with 60% Labor / 40% Material split
  const calculateTradeEstimate = (trade) => {
    const tradeConfig = TRADES[trade.tradeId];
    if (!tradeConfig || trade.services.length === 0) {
      return { laborMin: 0, laborMax: 0, materialMin: 0, materialMax: 0, totalMin: 0, totalMax: 0, breakdown: [] };
    }
    
    const qualityMult = tradeConfig.qualityMultipliers[trade.quality] || 1.0;
    let totalLaborMin = 0, totalLaborMax = 0;
    let totalMaterialMin = 0, totalMaterialMax = 0;
    const breakdown = [];
    
    trade.services.forEach(serviceId => {
      const service = tradeConfig.services.find(s => s.id === serviceId);
      if (service) {
        // Calculate base cost then split 60/40
        const baseCost = trade.sqft * service.baseRate * qualityMult;
        
        const laborBase = baseCost * LABOR_SPLIT;
        const materialBase = baseCost * MATERIAL_SPLIT;
        
        // Apply ±10% variance for estimates
        const laborMin = Math.round(laborBase * 0.9);
        const laborMax = Math.round(laborBase * 1.1);
        const materialMin = Math.round(materialBase * 0.9);
        const materialMax = Math.round(materialBase * 1.1);
        
        totalLaborMin += laborMin;
        totalLaborMax += laborMax;
        totalMaterialMin += materialMin;
        totalMaterialMax += materialMax;
        
        breakdown.push({
          service: service.name,
          laborMin, laborMax,
          materialMin, materialMax,
          totalMin: laborMin + materialMin,
          totalMax: laborMax + materialMax
        });
      }
    });
    
    return {
      laborMin: totalLaborMin,
      laborMax: totalLaborMax,
      materialMin: totalMaterialMin,
      materialMax: totalMaterialMax,
      totalMin: totalLaborMin + totalMaterialMin,
      totalMax: totalLaborMax + totalMaterialMax,
      breakdown
    };
  };

  // Calculate grand totals
  const grandTotals = useMemo(() => {
    let laborMin = 0, laborMax = 0;
    let materialMin = 0, materialMax = 0;
    
    selectedTrades.forEach(trade => {
      const est = calculateTradeEstimate(trade);
      laborMin += est.laborMin;
      laborMax += est.laborMax;
      materialMin += est.materialMin;
      materialMax += est.materialMax;
    });
    
    // Apply location surcharge
    if (zipCode && !isOKCArea) {
      laborMin = Math.round(laborMin * 1.15);
      laborMax = Math.round(laborMax * 1.15);
      materialMin = Math.round(materialMin * 1.15);
      materialMax = Math.round(materialMax * 1.15);
    }
    
    return {
      laborMin, laborMax,
      materialMin, materialMax,
      totalMin: laborMin + materialMin,
      totalMax: laborMax + materialMax
    };
  }, [selectedTrades, zipCode, isOKCArea]);

  const handleSubmitRequest = async () => {
    if (!contactInfo.name || !contactInfo.email || !contactInfo.phone) {
      toast.error("Please fill in your contact information");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates/request`, {
        projectType,
        trades: selectedTrades.map(t => ({
          trade: TRADES[t.tradeId]?.name,
          services: t.services.map(sId => TRADES[t.tradeId]?.services.find(s => s.id === sId)?.name),
          quality: t.quality,
          sqft: t.sqft,
          estimate: calculateTradeEstimate(t)
        })),
        postalCode: zipCode,
        ...contactInfo,
        labor_cost_min: grandTotals.laborMin,
        labor_cost_max: grandTotals.laborMax,
        material_cost_min: grandTotals.materialMin,
        material_cost_max: grandTotals.materialMax,
        estimate_min: grandTotals.totalMin,
        estimate_max: grandTotals.totalMax
      });
      toast.success("Your estimate request has been submitted!");
      setStep(4);
    } catch (error) {
      toast.error("Failed to submit. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-black pt-32 pb-20 relative overflow-hidden">
        {/* Background logo */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${LOGO_URL})`,
            backgroundSize: '300px',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.05,
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">Free Estimate</p>
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-6">
              Build Your Custom <span className="text-orange-500">Estimate</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Select your trades, choose multiple services, set quality levels, and get a detailed 
              breakdown of labor and material costs.
            </p>
          </div>
        </div>
      </section>

      {/* Estimator Tool */}
      <section className="py-16 md:py-24 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-gray-500'
                }`}>
                  {step > s ? <CheckCircle className="h-5 w-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 md:w-24 h-1 ${step > s ? 'bg-orange-500' : 'bg-zinc-800'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Project Type */}
          {step === 1 && (
            <Card className="bg-zinc-900/50 border border-zinc-800 backdrop-blur-sm">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl font-bold text-white">What type of project is this?</CardTitle>
                <p className="text-gray-500">Select the category that best describes your project</p>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {projectTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setProjectType(type.value);
                        setStep(2);
                      }}
                      className={`p-6 border-2 text-center transition-all hover:border-orange-500 hover:shadow-lg hover:shadow-orange-500/10 ${
                        projectType === type.value ? 'border-orange-500 bg-orange-500/10' : 'border-zinc-700 bg-zinc-800/50'
                      }`}
                    >
                      <type.icon className="h-10 w-10 text-orange-500 mx-auto mb-3" />
                      <h4 className="font-bold text-white">{type.label}</h4>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Select Trades & Configure */}
          {step === 2 && (
            <div className="space-y-6">
              <Card className="bg-zinc-900/50 border border-zinc-800 backdrop-blur-sm">
                <CardHeader className="border-b border-zinc-800">
                  <CardTitle className="text-2xl font-bold text-white">Select Trades & Services</CardTitle>
                  <p className="text-gray-500">Add trades, select multiple services per trade, and set quality levels</p>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Trade Selector */}
                  <div className="mb-8">
                    <Label className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-3 block">
                      Add a Trade
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(TRADES).map(([id, trade]) => (
                        <Button
                          key={id}
                          variant="outline"
                          size="sm"
                          onClick={() => addTrade(id)}
                          disabled={selectedTrades.find(t => t.tradeId === id)}
                          className="border-2 border-zinc-700 text-white hover:border-orange-500 hover:bg-orange-500/10 disabled:opacity-50"
                        >
                          <span className="mr-2">{trade.icon}</span>
                          {trade.name}
                          <Plus className="ml-2 h-4 w-4" />
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Selected Trades */}
                  {selectedTrades.length === 0 ? (
                    <div className="text-center py-12 bg-zinc-800/50 border-2 border-dashed border-zinc-700">
                      <p className="text-gray-500">Click on trades above to add them to your estimate</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {selectedTrades.map((trade, index) => {
                        const tradeConfig = TRADES[trade.tradeId];
                        const estimate = calculateTradeEstimate(trade);
                        
                        return (
                          <Card key={trade.id} className="bg-zinc-800/50 border border-zinc-700">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl">{tradeConfig.icon}</span>
                                  <div>
                                    <h4 className="font-bold text-white">{tradeConfig.name}</h4>
                                    <p className="text-sm text-gray-500">Trade #{index + 1}</p>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeTrade(trade.id)}
                                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {/* Multi-Select Services */}
                              <div className="mb-4">
                                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                                  Select Services (Choose Multiple)
                                </Label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                                  {tradeConfig.services.map((service) => (
                                    <label
                                      key={service.id}
                                      className={`flex items-center gap-2 p-3 border-2 cursor-pointer transition-all ${
                                        trade.services.includes(service.id)
                                          ? 'border-orange-500 bg-orange-500/10'
                                          : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                                      }`}
                                    >
                                      <Checkbox
                                        checked={trade.services.includes(service.id)}
                                        onCheckedChange={() => toggleService(trade.id, service.id)}
                                        className="border-zinc-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                                      />
                                      <span className="text-sm font-medium text-gray-300">{service.name}</span>
                                    </label>
                                  ))}
                                </div>
                                
                                {/* Active Filter Chips */}
                                {trade.services.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    <span className="text-xs text-gray-500 py-1">Selected:</span>
                                    {trade.services.map(serviceId => {
                                      const service = tradeConfig.services.find(s => s.id === serviceId);
                                      return (
                                        <span
                                          key={serviceId}
                                          className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500/20 text-orange-400 text-sm font-medium"
                                        >
                                          {service?.name}
                                          <button
                                            onClick={() => removeService(trade.id, serviceId)}
                                            className="hover:text-orange-300"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                {/* Quality Level */}
                                <div>
                                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1 block">
                                    Material Quality
                                  </Label>
                                  <Select 
                                    value={trade.quality} 
                                    onValueChange={(v) => updateTrade(trade.id, 'quality', v)}
                                  >
                                    <SelectTrigger className="border-2 border-zinc-700 bg-zinc-800 text-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-800 border-zinc-700">
                                      {Object.entries(QUALITY_LABELS).map(([key, val]) => (
                                        <SelectItem key={key} value={key} className="text-white hover:bg-zinc-700">
                                          <div>
                                            <span className="font-medium">{val.label}</span>
                                            <span className="text-gray-500 text-xs ml-2">({val.description})</span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Square Footage */}
                                <div>
                                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1 block">
                                    Square Feet
                                  </Label>
                                  <Input
                                    type="number"
                                    value={trade.sqft}
                                    onChange={(e) => updateTrade(trade.id, 'sqft', parseInt(e.target.value) || 0)}
                                    className="border-2 border-zinc-700 bg-zinc-800 text-white"
                                    min={1}
                                  />
                                </div>

                                {/* Cost Summary */}
                                <div className="bg-zinc-800 p-3 border border-zinc-700">
                                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1 block">
                                    Cost Breakdown (60/40 Split)
                                  </Label>
                                  {trade.services.length > 0 ? (
                                    <div className="space-y-1 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-400 flex items-center gap-1">
                                          <Wrench className="h-3 w-3" /> Labor (60%):
                                        </span>
                                        <span className="font-semibold text-blue-400">
                                          {formatCurrency(estimate.laborMin)}-{formatCurrency(estimate.laborMax)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-400 flex items-center gap-1">
                                          <Package className="h-3 w-3" /> Material (40%):
                                        </span>
                                        <span className="font-semibold text-green-400">
                                          {formatCurrency(estimate.materialMin)}-{formatCurrency(estimate.materialMax)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between pt-1 border-t border-zinc-700">
                                        <span className="font-semibold text-white">Total:</span>
                                        <span className="font-bold text-orange-500">
                                          {formatCurrency(estimate.totalMin)}-{formatCurrency(estimate.totalMax)}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-gray-500 text-sm">Select services</span>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {/* Zip Code */}
                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <Label className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      Project Zip Code
                    </Label>
                    <div className="flex gap-4 items-start">
                      <div className="flex-1 max-w-xs">
                        <Input
                          value={zipCode}
                          onChange={(e) => setZipCode(e.target.value)}
                          placeholder="73102"
                          className="border-2 border-zinc-700 bg-zinc-800 text-white"
                          maxLength={5}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          <Info className="inline h-3 w-3 mr-1" />
                          OKC area (73xxx) = standard pricing
                        </p>
                      </div>
                      {zipCode && !isOKCArea && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/20 text-orange-400 text-sm border border-orange-500/30">
                          <AlertCircle className="h-4 w-4" />
                          +15% travel surcharge applied
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Running Total with Labor/Material Breakdown */}
              {selectedTrades.length > 0 && (
                <Card className="bg-black border border-orange-500/30 shadow-lg shadow-orange-500/10">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded font-semibold">60% LABOR / 40% MATERIALS</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                      <div className="text-center md:text-left">
                        <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Labor Cost (60%)</p>
                        <div className="text-xl font-bold text-blue-400 flex items-center justify-center md:justify-start gap-2">
                          <Wrench className="h-4 w-4" />
                          {formatCurrency(grandTotals.laborMin)} - {formatCurrency(grandTotals.laborMax)}
                        </div>
                      </div>
                      <div className="text-center md:text-left">
                        <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Material Cost (40%)</p>
                        <div className="text-xl font-bold text-green-400 flex items-center justify-center md:justify-start gap-2">
                          <Package className="h-4 w-4" />
                          {formatCurrency(grandTotals.materialMin)} - {formatCurrency(grandTotals.materialMax)}
                        </div>
                      </div>
                      <div className="text-center md:text-left md:col-span-2">
                        <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Estimated Total</p>
                        <div className="text-2xl md:text-3xl font-black text-orange-500">
                          {formatCurrency(grandTotals.totalMin)} - {formatCurrency(grandTotals.totalMax)}
                        </div>
                        <p className="text-gray-600 text-xs mt-1">
                          {selectedTrades.length} trade{selectedTrades.length !== 1 ? 's' : ''} • 
                          {selectedTrades.reduce((sum, t) => sum + t.services.length, 0)} services
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="border-zinc-700 text-gray-300 hover:bg-zinc-800 hover:text-white"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={() => {
                          if (selectedTrades.length === 0) {
                            toast.error("Please add at least one trade");
                            return;
                          }
                          if (selectedTrades.some(t => t.services.length === 0)) {
                            toast.error("Please select at least one service for each trade");
                            return;
                          }
                          if (!zipCode) {
                            toast.error("Please enter your zip code");
                            return;
                          }
                          setStep(3);
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8"
                      >
                        Review Estimate
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 3: Review & Submit */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Itemized Bill */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="border-b border-slate-100 bg-slate-50">
                  <CardTitle className="text-2xl font-bold">Your Itemized Estimate</CardTitle>
                  <p className="text-slate-500">Detailed breakdown using <span className="font-semibold text-amber-600">60% Labor / 40% Materials</span> split</p>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Trade / Services</th>
                        <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Quality</th>
                        <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Sq Ft</th>
                        <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-blue-600">
                          <Wrench className="inline h-3 w-3 mr-1" />Labor (60%)
                        </th>
                        <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-green-600">
                          <Package className="inline h-3 w-3 mr-1" />Material (40%)
                        </th>
                        <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTrades.map((trade, index) => {
                        const tradeConfig = TRADES[trade.tradeId];
                        const estimate = calculateTradeEstimate(trade);
                        
                        return (
                          <tr key={trade.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-4">
                              <div className="flex items-start gap-3">
                                <span className="text-xl">{tradeConfig.icon}</span>
                                <div>
                                  <p className="font-semibold text-slate-900">{tradeConfig.name}</p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {trade.services.map(serviceId => {
                                      const service = tradeConfig.services.find(s => s.id === serviceId);
                                      return (
                                        <span key={serviceId} className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs">
                                          {service?.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 text-xs font-semibold uppercase ${
                                trade.quality === 'premium' ? 'bg-amber-100 text-amber-800' :
                                trade.quality === 'standard' ? 'bg-blue-100 text-blue-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {QUALITY_LABELS[trade.quality].label}
                              </span>
                            </td>
                            <td className="p-4 text-right font-mono text-slate-700">
                              {trade.sqft.toLocaleString()}
                            </td>
                            <td className="p-4 text-right">
                              <span className="font-semibold text-blue-600">
                                {formatCurrency(estimate.laborMin)} - {formatCurrency(estimate.laborMax)}
                              </span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="font-semibold text-green-600">
                                {formatCurrency(estimate.materialMin)} - {formatCurrency(estimate.materialMax)}
                              </span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="font-bold text-slate-900">
                                {formatCurrency(estimate.totalMin)} - {formatCurrency(estimate.totalMax)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-900 text-white">
                      {!isOKCArea && zipCode && (
                        <tr className="border-b border-slate-700">
                          <td colSpan={5} className="p-4 text-right text-slate-400">
                            Travel Surcharge (Out of Area)
                          </td>
                          <td className="p-4 text-right text-amber-400 font-semibold">+15%</td>
                        </tr>
                      )}
                      <tr className="border-b border-slate-700">
                        <td colSpan={3} className="p-4 text-right font-semibold text-slate-400">Subtotals</td>
                        <td className="p-4 text-right">
                          <span className="font-bold text-blue-400">
                            {formatCurrency(grandTotals.laborMin)} - {formatCurrency(grandTotals.laborMax)}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-bold text-green-400">
                            {formatCurrency(grandTotals.materialMin)} - {formatCurrency(grandTotals.materialMax)}
                          </span>
                        </td>
                        <td className="p-4"></td>
                      </tr>
                      <tr>
                        <td colSpan={5} className="p-4 text-right font-bold text-lg">
                          ESTIMATED TOTAL
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-2xl font-black text-amber-500">
                            {formatCurrency(grandTotals.totalMin)} - {formatCurrency(grandTotals.totalMax)}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </CardContent>
              </Card>

              {/* Cost Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-2 border-blue-200 bg-blue-50">
                  <CardContent className="p-6 text-center">
                    <Wrench className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-1">Labor Cost (60%)</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(grandTotals.laborMin)} - {formatCurrency(grandTotals.laborMax)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="p-6 text-center">
                    <Package className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold uppercase tracking-wider text-green-600 mb-1">Material Cost (40%)</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(grandTotals.materialMin)} - {formatCurrency(grandTotals.materialMax)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-200 bg-amber-50">
                  <CardContent className="p-6 text-center">
                    <Calculator className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                    <p className="text-sm font-semibold uppercase tracking-wider text-amber-600 mb-1">Combined Total</p>
                    <p className="text-2xl font-bold text-amber-700">
                      {formatCurrency(grandTotals.totalMin)} - {formatCurrency(grandTotals.totalMax)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-xl font-bold">Get Your Detailed Quote</CardTitle>
                  <p className="text-slate-500">Enter your contact info and we'll reach out within 24 hours</p>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-1 block">Name *</Label>
                      <Input
                        value={contactInfo.name}
                        onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                        placeholder="Your name"
                        className="border-2"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-1 block">Phone *</Label>
                      <Input
                        value={contactInfo.phone}
                        onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                        placeholder="(405) 555-0123"
                        className="border-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1 block">Email *</Label>
                    <Input
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                      placeholder="your@email.com"
                      className="border-2"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1 block">Additional Details</Label>
                    <Textarea
                      value={contactInfo.details}
                      onChange={(e) => setContactInfo({ ...contactInfo, details: e.target.value })}
                      placeholder="Tell us more about your project..."
                      className="border-2"
                    />
                  </div>
                  
                  <div className="flex gap-4 pt-4">
                    <Button variant="outline" onClick={() => setStep(2)} className="border-2">
                      Back to Edit
                    </Button>
                    <Button
                      onClick={handleSubmitRequest}
                      disabled={submitting}
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                    >
                      {submitting ? "Submitting..." : "Submit for Detailed Quote"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Call CTA */}
              <div className="text-center">
                <p className="text-slate-600 mb-2">Questions? Call us directly:</p>
                <a href="tel:4052562176" className="text-2xl font-bold text-amber-600 hover:text-amber-700">
                  <Phone className="inline h-6 w-6 mr-2" />
                  (405) 256-2176
                </a>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <Card className="bg-zinc-900/50 border border-zinc-800 text-center backdrop-blur-sm">
              <CardContent className="p-12">
                <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-orange-500" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">Estimate Submitted!</h2>
                <p className="text-lg text-gray-400 mb-8 max-w-md mx-auto">
                  Thank you! Our team will review your project details and contact you 
                  within 24 hours with a comprehensive quote.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/">
                    <Button variant="outline" className="px-8 border-zinc-700 text-gray-300 hover:bg-zinc-800">Return Home</Button>
                  </Link>
                  <Link to="/portfolio">
                    <Button className="px-8 bg-orange-500 hover:bg-orange-600 text-white">View Our Work</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EstimatorPage;
